<script setup lang="ts">
//#region imports
import { cloneDeep, get } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useApi } from '@/api';
import { useActivitiesLanguage } from '@/views/CRM/composables/useActivitiesLanguage';
import type { EmitsToListeners } from '@/types/core';
import type { CrmAction, CrmActionGroup, CrmActivity } from '@/types/crm';
//#endregion

//#region types
type ProjectOption = {
  id: string | number;
  name: string;
};

interface Props {
  activity?: CrmActivity;
  currentBrand?: string;
  group?: CrmActionGroup | null;
  payload?: {
    errors?: {
      call?: Array<Record<string, unknown> | null>;
    };
  };
}

interface Emits {
  save: [actionGroup: CrmActionGroup | null, actions: CrmAction[], actionType: 'call'];
  close: [];
}

export interface AddCallCenterActionParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  activity: () => ({ action_groups: [] } as unknown as CrmActivity),
  currentBrand: '',
  group: null,
});
const emit = defineEmits<Emits>();
//#endregion

//#region const
const api = useApi();
const store = useStore();
//#endregion

//#region composables
const isVisible = ref(false);
const index = ref(-1);
const actionGroup = ref<CrmActionGroup | null>(null);
const actionGroupIndex = ref(-1);
const actions = ref<CrmAction[]>([]);
const errors = ref<Array<Record<string, unknown> | null>>([]);
const call_center_project_id = ref<number | null>(null);
const projectOptions = ref<ProjectOption[]>([]);
const name = ref('');

const languagesFromStore = computed(() => store.state.misc?.languages || []);
const { setActionsValue } = useActivitiesLanguage({
  actions,
  index,
  actionGroupIndex,
  languagesFromStore,
});
//#endregion

//#region computed
const cellError = computed(() => String(get(errors.value, [0, 'call_center_project_id'], '')));
//#endregion

//#region methods
function getDefaultForm(): CrmAction {
  return {
    name: '',
    type: 'call',
    language_code: 'en',
    is_default: true,
    call_center_project_id: null,
  } as unknown as CrmAction;
}

function onChangeProject(id: string | number) {
  const project = projectOptions.value.find((item) => item.id === id);
  if (project) {
    name.value = project.name;
  }
}

async function open() {
  const targetGroup = props.group;
  const payload = props.payload ?? null;

  if (!targetGroup) {
    isVisible.value = true;
    return;
  }

  const res = await api.getCallCenterProjects({ site_id: props.currentBrand });
  projectOptions.value = res.data.payload || [];

  actionGroup.value = targetGroup;
  actionGroupIndex.value = props.activity.action_groups.indexOf(targetGroup);

  const sourceActions = (props.activity.action_groups[actionGroupIndex.value]?.actions || [])
    .filter((item) => item.type === 'call');

  if (!sourceActions.length) {
    sourceActions.push(getDefaultForm());
  }

  actions.value = cloneDeep(sourceActions);
  call_center_project_id.value = get(actions.value, [0, 'call_center_project_id'], null) as number | null;
  index.value = 0;
  isVisible.value = true;
  errors.value = [];

  if (payload?.errors?.call) {
    errors.value = actions.value.map((_, i) => payload.errors?.call?.[i] || null);
  }
}

function close() {
  isVisible.value = false;
  emit('close');
}

function handleSave() {
  emit('save', actionGroup.value, actions.value, 'call');
  close();
}
//#endregion

//#region watch
watch(call_center_project_id, (value) => {
  setActionsValue('call_center_project_id', value);
});

watch(name, (value) => {
  setActionsValue('name', value);
});
//#endregion

//#region hooks
defineExpose({
  open,
  close,
});
//#endregion
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="$t('crm.activities.actions.action_popup.call_center_title')"
    :width="560"
    @close="close()"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiLabel
          :label="$t('crm.activities.actions.action_popup.call_center_hint')"
        />
        <UiSelectElement
          v-model="call_center_project_id"
          :placeholder="$t('crm.activities.actions.action_popup.call_center_select_placeholder')"
          :label="$t('crm.activities.actions.action_popup.call_center_select')"
          :list="projectOptions"
          filterable
          :error="cellError"
          size="big"
          @change="onChangeProject"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          @click="handleSave()"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
